import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/authSlice";
import { useGetTimelineFeedQuery } from "../../../../app/communicationApiSlice";
import PostComposer from "./PostComposer";
import PostCard from "./PostCard";

const PER_PAGE = 15;

const SCOPE_TABS = [
  { label: "All", value: "" },
  { label: "Parents", value: "parents" },
  { label: "Teachers", value: "teachers" },
  { label: "Class", value: "class_parents" },
];

const FEED_CACHE_KEY = 'comm_feed_cache_v1';

function readFeedCache() {
  try { return JSON.parse(localStorage.getItem(FEED_CACHE_KEY) || 'null'); } catch { return null; }
}
function writeFeedCache(posts) {
  try { localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(posts)); } catch { /* quota */ }
}

function useInfiniteTimeline(scope) {
  const [page, setPage] = useState(1);
  const [allPosts, setAllPosts] = useState(() => {
    // Seed from localStorage so the feed shows instantly while fetching
    if (page === 1 && !scope) return readFeedCache() || [];
    return [];
  });
  const [hasMore, setHasMore] = useState(true);

  const { data, isFetching, isError } = useGetTimelineFeedQuery(
    { page, per_page: PER_PAGE, scope: scope || undefined },
    { refetchOnMountOrArgChange: true }
  );

  // Merge incoming page into accumulated list
  useEffect(() => {
    if (!data) return;
    setAllPosts((prev) => {
      const existingIds = new Set(prev.map((p) => p.id));
      const newPosts = (data.posts || []).filter((p) => !existingIds.has(p.id));
      const merged = page === 1 ? (data.posts || []) : [...prev, ...newPosts];
      // Persist page 1 of the default scope for offline use
      if (page === 1 && !scope) writeFeedCache(merged);
      return merged;
    });
    setHasMore(data.page < data.pages);
  }, [data, page, scope]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) setPage((p) => p + 1);
  }, [isFetching, hasMore]);

  const reset = useCallback(() => {
    setPage(1);
    setAllPosts([]);
    setHasMore(true);
  }, []);

  return { allPosts, isFetching, isError, hasMore, loadMore, reset };
}

export default function FeedPage() {
  const user = useSelector(selectCurrentUser);
  const roles = user?.roles?.map((r) => r?.role?.name).filter(Boolean) || [];
  const isAdmin = roles.includes("admin");
  const isTeacher = roles.includes("teacher");
  const isParent = roles.includes("parent");

  // Scope filtering — parents only see parents tab; teachers only see teachers
  const availableTabs = SCOPE_TABS.filter((t) => {
    if (t.value === "teachers") return isAdmin || isTeacher;
    if (t.value === "class_parents") return isAdmin || isTeacher;
    if (t.value === "parents") return isAdmin || isParent;
    return true; // "All"
  });

  const [scopeIndex, setScopeIndex] = useState(0);
  const scope = availableTabs[scopeIndex]?.value || "";

  const { allPosts, isFetching, isError, hasMore, loadMore, reset } =
    useInfiniteTimeline(scope);

  // Reset when scope changes
  const prevScope = useRef(scope);
  useEffect(() => {
    if (prevScope.current !== scope) {
      prevScope.current = scope;
      reset();
    }
  }, [scope, reset]);

  // IntersectionObserver sentinel
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  const handlePosted = useCallback(() => {
    reset();
  }, [reset]);

  return (
    <Box>
      <Stack spacing={2.5}>
        {/* Page title */}
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Feed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Updates, announcements, and news from your school community.
          </Typography>
        </Box>

        {/* Composer (admin + teachers only) */}
        {(isAdmin || isTeacher) && <PostComposer onPosted={handlePosted} />}

        {/* Scope filter tabs */}
        {availableTabs.length > 1 && (
          <Box>
            <Tabs
              value={scopeIndex}
              onChange={(_, v) => setScopeIndex(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              {availableTabs.map((t) => (
                <Tab key={t.value} label={t.label} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Feed */}
        <Stack spacing={2}>
          {isError && (
            <Alert severity="error">
              Could not load the feed. Please refresh.
            </Alert>
          )}

          {allPosts.length === 0 && !isFetching && !isError && (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                color: "text.secondary",
              }}
            >
              <Typography variant="h2" sx={{ mb: 1 }}>
                📭
              </Typography>
              <Typography fontWeight={600}>Nothing here yet</Typography>
              <Typography variant="body2">
                {isAdmin || isTeacher
                  ? "Be the first to share an update with your community."
                  : "No posts have been shared with you yet."}
              </Typography>
            </Box>
          )}

          {allPosts.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}

          {/* Infinite scroll sentinel */}
          <Box ref={sentinelRef} sx={{ height: 1 }} />

          {isFetching && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!hasMore && allPosts.length > 0 && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Divider>
                <Typography variant="caption" color="text.disabled">
                  You're all caught up
                </Typography>
              </Divider>
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
